import { jsx as _jsx } from "react/jsx-runtime";
import { createContext, useContext, useEffect, useState } from 'react';
function isTheme(value) {
    return value === 'light' || value === 'dark' || value === 'system';
}
const DarkModeContext = /*#__PURE__*/ createContext(undefined);
export function DarkModeProvider({ children }) {
    const [theme, setThemeState] = useState('light');
    const [systemPreference, setSystemPreference] = useState('light');
    // Listen to system preference changes
    useEffect(()=>{
        const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
        const handleChange = (e)=>{
            setSystemPreference(e.matches ? 'dark' : 'light');
        };
        // Set initial value
        setSystemPreference(mediaQuery.matches ? 'dark' : 'light');
        // Listen for changes
        mediaQuery.addEventListener('change', handleChange);
        return ()=>mediaQuery.removeEventListener('change', handleChange);
    }, []);
    // Initialize theme from localStorage
    useEffect(()=>{
        const savedTheme = localStorage.getItem('theme');
        if (isTheme(savedTheme)) {
            setThemeState(savedTheme);
        } else {
            // Check for legacy darkMode setting
            const legacyDarkMode = localStorage.getItem('darkMode');
            if (legacyDarkMode === 'true') {
                setThemeState('dark');
            } else {
                setThemeState('light');
            }
        }
    }, []);
    // Compute effective dark mode based on theme and system preference
    const isDarkMode = theme === 'system' ? systemPreference === 'dark' : theme === 'dark';
    // Update the document class when effective theme changes
    useEffect(()=>{
        if (isDarkMode) {
            document.documentElement.classList.add('dark');
        } else {
            document.documentElement.classList.remove('dark');
        }
    }, [
        isDarkMode
    ]);
    const setTheme = (newTheme)=>{
        setThemeState(newTheme);
        localStorage.setItem('theme', newTheme);
    };
    const toggleDarkMode = ()=>{
        setTheme(isDarkMode ? 'light' : 'dark');
    };
    const setDarkMode = (enabled)=>{
        setTheme(enabled ? 'dark' : 'light');
    };
    const value = {
        theme,
        isDarkMode,
        setTheme,
        toggleDarkMode,
        setDarkMode
    };
    return /*#__PURE__*/ _jsx(DarkModeContext.Provider, {
        value: value,
        children: children
    });
}
export function useDarkMode() {
    const context = useContext(DarkModeContext);
    if (context === undefined) {
        throw new Error('useDarkMode must be used within a DarkModeProvider');
    }
    return context;
}
